// src/components/admin/CommissionManagement.js
import { useState, useMemo } from 'react';
import { doc, updateDoc, Timestamp, increment, writeBatch } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { DollarSign, Users, Check, ChevronDown, ChevronUp, Loader2, Search, Calendar } from 'lucide-react';

export function CommissionManagement({ reps, orders, loading }) {
  const [expandedRep, setExpandedRep] = useState(null);
  const [payingRep, setPayingRep] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentNote, setPaymentNote] = useState('');
  const [processing, setProcessing] = useState(false);
  const [markingOrders, setMarkingOrders] = useState(false);

  // Calculate commission data per rep
  const repCommissionData = useMemo(() => {
    return reps.map(rep => {
      const repOrders = orders.filter(o => o.repId === rep.id);
      const unpaidOrders = repOrders.filter(o => o.commission > 0 && !o.commissionPaid);
      const paidOrders = repOrders.filter(o => o.commission > 0 && o.commissionPaid);
      
      const totalEarned = repOrders.reduce((sum, o) => sum + (o.commission || 0), 0);
      const totalUnpaid = unpaidOrders.reduce((sum, o) => sum + (o.commission || 0), 0);
      const totalPaid = rep.totalCommissionPaid || 0;

      return {
        ...rep,
        repOrders,
        unpaidOrders,
        paidOrders,
        totalEarned,
        totalUnpaid,
        totalPaid,
        owed: totalEarned - totalPaid
      };
    }).filter(rep => rep.totalEarned > 0 || rep.unpaidOrders.length > 0);
  }, [reps, orders]);

  const totalOwedAllReps = repCommissionData.reduce((sum, rep) => sum + rep.owed, 0);

  const handleMarkOrdersPaid = async (rep, orderIds) => {
    setMarkingOrders(true);
    try {
      const batch = writeBatch(db);
      let totalMarked = 0;

      for (const orderId of orderIds) {
        const order = rep.unpaidOrders.find(o => o.id === orderId);
        if (order) {
          batch.update(doc(db, 'orders', orderId), {
            commissionPaid: true,
            commissionPaidAt: Timestamp.now()
          });
          totalMarked += order.commission || 0;
        }
      }

      // Update rep's total paid
      batch.update(doc(db, 'users', rep.id), {
        totalCommissionPaid: increment(totalMarked)
      });

      await batch.commit();
    } catch (err) {
      console.error('Error marking orders paid:', err);
      alert('Failed to mark orders as paid');
    } finally {
      setMarkingOrders(false);
    }
  };

  const handlePayCommission = async (rep) => {
    const amount = parseFloat(paymentAmount);
    if (isNaN(amount) || amount <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    setProcessing(true);
    try {
      // Update rep's total paid
      await updateDoc(doc(db, 'users', rep.id), {
        totalCommissionPaid: increment(amount),
        lastCommissionPayment: {
          amount,
          note: paymentNote,
          paidAt: Timestamp.now()
        }
      });

      // Mark unpaid orders as paid (up to the amount)
      let remaining = amount;
      const batch = writeBatch(db);
      
      for (const order of rep.unpaidOrders) {
        if (remaining >= order.commission) {
          batch.update(doc(db, 'orders', order.id), {
            commissionPaid: true,
            commissionPaidAt: Timestamp.now()
          });
          remaining -= order.commission;
        } else {
          break;
        }
      }

      await batch.commit();

      setPayingRep(null);
      setPaymentAmount('');
      setPaymentNote('');
      alert('Payment recorded successfully');
    } catch (err) {
      console.error('Error recording payment:', err);
      alert('Failed to record payment');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <Loader2 className="animate-spin mx-auto" size={32} />
        <p className="text-gray-500 mt-4">Loading commission data...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Commission Management</h2>
        <div className="bg-green-100 text-green-800 px-4 py-2 rounded-lg">
          <span className="text-sm">Total Owed:</span>
          <span className="ml-2 font-bold text-lg">${totalOwedAllReps.toFixed(2)}</span>
        </div>
      </div>

      {repCommissionData.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <DollarSign size={48} className="text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No commission data yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {repCommissionData.map(rep => {
            const isExpanded = expandedRep === rep.id;
            
            return (
              <div key={rep.id} className="bg-white rounded-lg shadow overflow-hidden">
                {/* Rep Header */}
                <div
                  className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                  onClick={() => setExpandedRep(isExpanded ? null : rep.id)}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <DollarSign size={20} className="text-green-600" />
                    </div>
                    <div>
                      <p className="font-semibold">{rep.name}</p>
                      <p className="text-sm text-gray-500">
                        {rep.commissionType === 'percentage' 
                          ? `${rep.commissionRate}% commission`
                          : `$${rep.commissionRate}/unit`}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <p className="text-sm text-gray-500">Total Earned</p>
                      <p className="font-medium">${rep.totalEarned.toFixed(2)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500">Paid</p>
                      <p className="font-medium">${rep.totalPaid.toFixed(2)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500">Owed</p>
                      <p className="font-bold text-green-600">${rep.owed.toFixed(2)}</p>
                    </div>
                    {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="border-t p-4">
                    {/* Payment Form */}
                    {payingRep === rep.id ? (
                      <div className="bg-gray-50 p-4 rounded-lg mb-4">
                        <h4 className="font-semibold mb-3">Record Payment</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium mb-1">Amount</label>
                            <input
                              type="number"
                              step="0.01"
                              value={paymentAmount}
                              onChange={(e) => setPaymentAmount(e.target.value)}
                              placeholder={`Max: $${rep.owed.toFixed(2)}`}
                              className="w-full px-3 py-2 border rounded-lg"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-1">Note (optional)</label>
                            <input
                              type="text"
                              value={paymentNote}
                              onChange={(e) => setPaymentNote(e.target.value)}
                              placeholder="Check #, Venmo, etc."
                              className="w-full px-3 py-2 border rounded-lg"
                            />
                          </div>
                          <div className="flex items-end gap-2">
                            <button
                              onClick={() => handlePayCommission(rep)}
                              disabled={processing}
                              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 flex items-center gap-2"
                            >
                              {processing ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                              Record Payment
                            </button>
                            <button
                              onClick={() => setPayingRep(null)}
                              className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="mb-4">
                        <button
                          onClick={() => {
                            setPayingRep(rep.id);
                            setPaymentAmount(rep.owed.toFixed(2));
                          }}
                          disabled={rep.owed <= 0}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400"
                        >
                          Pay Commission (${rep.owed.toFixed(2)})
                        </button>
                      </div>
                    )}

                    {/* Unpaid Orders */}
                    <div className="mb-6">
                      <div className="flex justify-between items-center mb-3">
                        <h4 className="font-semibold">Unpaid Orders ({rep.unpaidOrders.length})</h4>
                        {rep.unpaidOrders.length > 0 && (
                          <button
                            onClick={() => handleMarkOrdersPaid(rep, rep.unpaidOrders.map(o => o.id))}
                            disabled={markingOrders}
                            className="text-sm px-3 py-1 bg-gray-100 rounded hover:bg-gray-200 flex items-center gap-1"
                          >
                            {markingOrders ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                            Mark All Paid
                          </button>
                        )}
                      </div>
                      
                      {rep.unpaidOrders.length === 0 ? (
                        <p className="text-sm text-gray-500">No unpaid orders</p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-3 py-2 text-left">Order</th>
                                <th className="px-3 py-2 text-left">Customer</th>
                                <th className="px-3 py-2 text-left">Date</th>
                                <th className="px-3 py-2 text-right">Order Total</th>
                                <th className="px-3 py-2 text-right">Commission</th>
                                <th className="px-3 py-2 text-center">Action</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y">
                              {rep.unpaidOrders.map(order => {
                                const date = order.createdAt?.toDate?.() || new Date(order.createdAt);
                                return (
                                  <tr key={order.id}>
                                    <td className="px-3 py-2">#{order.id.slice(-8).toUpperCase()}</td>
                                    <td className="px-3 py-2">{order.customerName}</td>
                                    <td className="px-3 py-2">{date.toLocaleDateString()}</td>
                                    <td className="px-3 py-2 text-right">${order.subtotal?.toFixed(2)}</td>
                                    <td className="px-3 py-2 text-right font-medium text-green-600">
                                      ${order.commission?.toFixed(2)}
                                    </td>
                                    <td className="px-3 py-2 text-center">
                                      <button
                                        onClick={() => handleMarkOrdersPaid(rep, [order.id])}
                                        className="text-green-600 hover:underline text-xs"
                                      >
                                        Mark Paid
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>

                    {/* Recent Paid Orders */}
                    {rep.paidOrders.length > 0 && (
                      <div>
                        <h4 className="font-semibold mb-3">Recently Paid ({rep.paidOrders.length})</h4>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-3 py-2 text-left">Order</th>
                                <th className="px-3 py-2 text-left">Customer</th>
                                <th className="px-3 py-2 text-left">Date</th>
                                <th className="px-3 py-2 text-right">Commission</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y">
                              {rep.paidOrders.slice(0, 5).map(order => {
                                const date = order.createdAt?.toDate?.() || new Date(order.createdAt);
                                return (
                                  <tr key={order.id} className="text-gray-500">
                                    <td className="px-3 py-2">#{order.id.slice(-8).toUpperCase()}</td>
                                    <td className="px-3 py-2">{order.customerName}</td>
                                    <td className="px-3 py-2">{date.toLocaleDateString()}</td>
                                    <td className="px-3 py-2 text-right">${order.commission?.toFixed(2)}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
